import axios, { AxiosInstance, AxiosResponse } from "axios";
import { IApiResponseErrorType } from "./responseHandlers";
import qs from "qs";
import { getConfig } from "@config/index";
import {
  addAuthorizationRequestInterceptor,
  addExpiredAuthorizationTokenResponseInterceptor,
} from "@network/interceptors";
import { IApiClient, IAuthHelper } from "@network/interfaces";
import { errorHandler, successHandler } from "@network/responseHandlers";
import { AuthenticationService } from "@common/services/AuthenticationService";

const getClient = (baseUrl: string, client: IApiClient): AxiosInstance => {
  let axiosInstance = axios.create({
    baseURL: baseUrl,
    timeout: 30000,
    // Will convert the array to comma separated values per key
    paramsSerializer: (params) =>
      qs.stringify(params, { arrayFormat: "comma" }),
  });

  // Add Request Interceptor to add Authorization header
  axiosInstance = addAuthorizationRequestInterceptor(axiosInstance, client);

  // Add Response Interceptor for Authorization token expiry
  axiosInstance = addExpiredAuthorizationTokenResponseInterceptor(
    axiosInstance,
    client
  );

  return axiosInstance;
};

export interface IMisRequestProps {
  baseURL?: string;
  disableEnvelope?: boolean;
}

export type IDeleteType =
  | Promise<AxiosResponse<any>>
  | Promise<
      | {
          data?: undefined;
          headers?: undefined;
        }
      | {
          data: IApiResponseErrorType;
          headers: any;
          reqProps?: IMisRequestProps;
        }
    >;

export type IPatchType =
  | Promise<AxiosResponse<any>>
  | Promise<
      | {
          data?: undefined;
          headers?: undefined;
        }
      | {
          data: IApiResponseErrorType;
          headers: any;
          reqProps?: IMisRequestProps;
        }
    >;

export class ApiClientSinglton implements IApiClient {
  private baseUrl: string;
  private client: AxiosInstance;
  private authHelper?: IAuthHelper = undefined;

  constructor() {
    const baseUrl = getConfig().APP_BE_BASE_URL;
    this.client = getClient(baseUrl, this);
    this.baseUrl = baseUrl;
  }

  // TODO: Add response and error handlers here
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  public request = (config: any) => {
    /**
     * To handle api requests with different base url
     */
    if (config?.reqProps && config?.reqProps?.baseURL) {
      config.baseURL = config?.reqProps?.baseURL;
    }

    /*
    below if is to resolve auth token refresh issue,
    since retried API will have parsed data.
    */
    if (config._retry) {
      return this.client
        .request(config)
        .then((response) => Promise.resolve(response))
        .catch((error) => Promise.reject(error));
    }
    return this.client
      .request(config)
      .then((response) => Promise.resolve(successHandler(response)))
      .catch((error) => Promise.reject(errorHandler(error)));
  };

  public get = (url: string, params = {}, reqProps: IMisRequestProps = {}) => {
    return this.request({
      method: "GET",
      url,
      params,
      reqProps,
    });
  };

  public post = (
    url: string,
    data = {},
    params = {},
    reqProps: IMisRequestProps = {}
  ) => {
    return this.request({
      method: "POST",
      url,
      data,
      params,
      reqProps,
    });
  };

  public put = (
    url: string,
    data = {},
    params = {},
    reqProps: IMisRequestProps = {}
  ) => {
    return this.request({
      method: "PUT",
      url,
      data,
      params,
      reqProps,
    });
  };

  public patch = (
    url: string,
    data = {},
    params = {},
    reqProps: IMisRequestProps = {}
  ) => {
    return this.request({
      method: "PATCH",
      url,
      data,
      params,
      reqProps,
    });
  };

  public delete = (
    url: string,
    data = {},
    params = {},
    reqProps: IMisRequestProps = {}
  ) => {
    return this.request({
      method: "DELETE",
      url,
      data,
      params,
      reqProps,
    });
  };

  public getAuthHelper = (): IAuthHelper | undefined => this.authHelper;

  public setAuthHelper = (authHelper: IAuthHelper): void => {
    this.authHelper = authHelper;
  };
}

const apiClient = new ApiClientSinglton();
AuthenticationService.setAPIClient(apiClient);
export { apiClient as ApiClient };
